// Field / FormField (DESIGN §3.6.12): Label (`label` `fg-secondary`, 6 über dem Feld), „Optional“
// statt Pflichtstern, Hilfetext (`label` `fg-tertiary`), Fehler ersetzt den Hilfetext (`danger-fg`
// mit WarningCircle). Das Feld darin übernimmt id, aria-describedby und aria-invalid über Kontext.
import { useId, type ReactElement, type ReactNode } from 'react';
import { WarningCircleIcon } from '@phosphor-icons/react';
import {
  useController,
  type ControllerFieldState,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  type UseControllerProps,
} from 'react-hook-form';
import { FieldContext } from './_lib/fieldContext';
import { cn } from '../basis/kern';

export interface FieldProps {
  label: ReactNode;
  /** Hilfetext unter dem Feld; ein Fehler ersetzt ihn. */
  help?: ReactNode;
  error?: ReactNode;
  /** Zeigt „Optional“ neben dem Label (Pflichtfelder bekommen keinen Stern). */
  optional?: boolean;
  required?: boolean;
  /** Eigene id des Feldes (sonst erzeugt). */
  id?: string;
  /**
   * `group` für zusammengesetzte Felder (RadioGroup, SegmentedControl, RatingPills): Label als
   * Gruppenname statt `<label for>`.
   */
  group?: boolean;
  /** Label nur für Screenreader (z. B. in Filterleisten). */
  hideLabel?: boolean;
  className?: string;
  children: ReactNode;
}

export function Field({
  label,
  help,
  error,
  optional,
  required,
  id: idProp,
  group = false,
  hideLabel = false,
  className,
  children,
}: FieldProps) {
  const autoId = useId();
  const id = idProp ?? `field${autoId}`;
  const labelId = `${id}-label`;
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const hasError = error !== undefined && error !== null && error !== false && error !== '';
  const describedBy = hasError ? errorId : help ? helpId : undefined;

  const labelContent = (
    <>
      {label}
      {optional ? <span className="ml-1.5 text-fg-tertiary">Optional</span> : null}
    </>
  );
  const labelClass = cn('text-label text-fg-secondary', hideLabel && 'sr-only');

  return (
    <FieldContext.Provider value={{ id, labelId, describedBy, invalid: hasError, required }}>
      <div
        className={cn('flex min-w-0 flex-col gap-1.5', className)}
        {...(group
          ? { role: 'group', 'aria-labelledby': labelId, 'aria-describedby': describedBy }
          : {})}
      >
        {group ? (
          <span id={labelId} className={labelClass}>
            {labelContent}
          </span>
        ) : (
          <label id={labelId} htmlFor={id} className={labelClass}>
            {labelContent}
          </label>
        )}
        {children}
        {hasError ? (
          <p id={errorId} className="flex items-start gap-1.5 text-label text-danger-fg">
            <WarningCircleIcon aria-hidden="true" size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </p>
        ) : help ? (
          <p id={helpId} className="text-label text-fg-tertiary">
            {help}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

export interface FormFieldProps<TValues extends FieldValues, TName extends FieldPath<TValues>>
  extends UseControllerProps<TValues, TName>, Omit<FieldProps, 'children' | 'error' | 'id'> {
  render: (args: {
    field: ControllerRenderProps<TValues, TName>;
    fieldState: ControllerFieldState;
  }) => ReactElement;
}

/**
 * Feld an react-hook-form (+ zod über den Resolver des Formulars). Fehler erscheinen nach dem
 * Verlassen des Felds oder beim Absenden, nicht beim Tippen; RHF fokussiert beim Absenden das erste
 * fehlerhafte Feld über `field.ref`.
 */
export function FormField<TValues extends FieldValues, TName extends FieldPath<TValues>>({
  render,
  label,
  help,
  optional,
  required,
  group,
  hideLabel,
  className,
  ...controller
}: FormFieldProps<TValues, TName>) {
  const { field, fieldState, formState } = useController(controller);
  const showError = fieldState.error && (fieldState.isTouched || formState.isSubmitted);
  return (
    <Field
      label={label}
      help={help}
      optional={optional}
      required={required}
      group={group}
      hideLabel={hideLabel}
      className={className}
      error={showError ? (fieldState.error?.message ?? 'Invalid value') : undefined}
    >
      {render({ field, fieldState })}
    </Field>
  );
}
